// src/App.js
import { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Carousel from "bootstrap/js/dist/carousel";

const RAW_API_BASE = process.env.REACT_APP_API_BASE || "/api";
const API_BASE = String(RAW_API_BASE).replace(/\/+$/, "");
// -----------------------------------

function App() {
  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

    :root {
      /* Modern Deep Palette */
      --primary: #3b82f6;
      --primary-hover: #2563eb;
      --accent: #8b5cf6;
      --brand-gradient: linear-gradient(135deg, var(--primary), var(--accent));
      
      /* Glass & Surface */
      --glass-bg: rgba(17, 25, 40, 0.75);
      --glass-border: rgba(255, 255, 255, 0.08);
      --glass-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
      
      /* Text */
      --text-main: #ffffff;
      --text-muted: #94a3b8;
      
      /* Inputs */
      --input-bg: rgba(255, 255, 255, 0.05);
      --input-focus-ring: rgba(59, 130, 246, 0.5);
    }

    body {
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
      -webkit-font-smoothing: antialiased;
    }

    /* Background Carousel Layer */
    .bg-carousel { position: fixed; inset: 0; z-index: -2; }
    .bg-carousel .carousel-item { height: 100vh; }
    .bg-carousel img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      /* Subtle zoom effect for background */
      animation: zoomSlow 20s infinite alternate; 
    }

    @keyframes zoomSlow {
      from { transform: scale(1); }
      to { transform: scale(1.1); }
    }

    /* Scrim: Cinematic Vignette */
    .bg-scrim {
      position: fixed;
      inset: 0;
      z-index: -1;
      background: radial-gradient(circle at center, rgba(15, 23, 42, 0.4), rgba(2, 6, 23, 0.9));
      backdrop-filter: blur(3px);
    }

    /* Layout */
    .auth-center {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }

    /* The Glass Card */
    .wrapper {
      width: 100%;
      max-width: 440px;
      background: var(--glass-bg);
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border: 1px solid var(--glass-border);
      border-radius: 24px;
      padding: 40px;
      box-shadow: var(--glass-shadow);
      color: var(--text-main);
      
      /* Entrance Animation */
      animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    }

    @keyframes slideUpFade {
      from { opacity: 0; transform: translateY(30px) scale(0.95); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    /* Brand Header */
    .brand-row {
      text-align: center;
      margin-bottom: 32px;
    }
    .brand-logo {
      width: 56px;
      height: 56px;
      margin: 0 auto 16px;
      background: var(--brand-gradient);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 28px;
      color: white;
      box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.5);
    }
    .brand-title {
      font-size: 1.75rem;
      font-weight: 700;
      letter-spacing: -0.025em;
      margin: 0;
      background: linear-gradient(to right, #fff, #cbd5e1);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    .brand-subtitle {
      font-size: 0.9rem;
      color: var(--text-muted);
      margin-top: 6px;
    }

    /* Inputs */
    .input-group-modern {
      position: relative;
      margin-bottom: 20px;
    }
    
    .input-group-modern input {
      width: 100%;
      background: var(--input-bg);
      border: 1px solid transparent;
      border-radius: 12px;
      padding: 16px 16px 16px 48px; /* Left padding for icon */
      color: white;
      font-size: 0.95rem;
      transition: all 0.2s ease;
    }
    
    .input-group-modern input::placeholder {
      color: rgba(255,255,255, 0.4);
    }

    .input-group-modern input:focus {
      background: rgba(255, 255, 255, 0.1);
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 4px var(--input-focus-ring);
    }

    /* Icons inside Input */
    .input-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--text-muted);
      font-size: 1.1rem;
      pointer-events: none;
      transition: color 0.2s;
    }
    
    .input-group-modern input:focus ~ .input-icon {
      color: var(--primary);
    }

    /* Toggle Password Eye */
    .toggle-visibility {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      background: transparent;
      border: none;
      color: var(--text-muted);
      padding: 8px;
      cursor: pointer;
      border-radius: 50%;
      display: flex;
      align-items: center;
      transition: all 0.2s;
    }
    .toggle-visibility:hover {
      background: rgba(255,255,255,0.1);
      color: white;
    }

    /* Actions: Remember & Forgot */
    .action-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.875rem;
      margin-bottom: 24px;
      color: var(--text-muted);
    }
    .action-row a {
      color: var(--text-muted);
      text-decoration: none;
      transition: color 0.2s;
    }
    .action-row a:hover {
      color: white;
      text-decoration: underline;
    }
    
    /* Custom Checkbox */
    .custom-check {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
    }
    .custom-check input {
      accent-color: var(--primary);
      width: 16px;
      height: 16px;
    }

    /* Login Button */
    .btn-login {
      width: 100%;
      padding: 16px;
      border-radius: 12px;
      border: none;
      background: var(--brand-gradient);
      color: white;
      font-weight: 600;
      font-size: 1rem;
      letter-spacing: 0.025em;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }
    
    .btn-login:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 30px -10px var(--primary);
      filter: brightness(1.1);
      color: white;
    }
    
    .btn-login:active:not(:disabled) {
      transform: translateY(0);
    }

    .btn-login:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      filter: grayscale(0.5);
    }

    /* Error Box */
    .error-box {
      background: rgba(239, 68, 68, 0.15);
      border: 1px solid rgba(239, 68, 68, 0.3);
      color: #fca5a5;
      padding: 12px;
      border-radius: 8px;
      font-size: 0.875rem;
      text-align: center;
      margin-bottom: 20px;
      animation: shake 0.4s ease-in-out;
    }

    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-4px); }
      75% { transform: translateX(4px); }
    }

    .footer-note {
      margin-top: 24px;
      text-align: center;
      font-size: 0.8rem;
      color: rgba(255,255,255,0.3);
    }
  `;

  const IMAGES = [
    "https://images.pexels.com/photos/8322786/pexels-photo-8322786.jpeg?auto=compress&cs=tinysrgb&w=1600",
    "https://images.pexels.com/photos/31374680/pexels-photo-31374680.jpeg?auto=compress&cs=tinysrgb&w=1600",
    "https://images.pexels.com/photos/11769075/pexels-photo-11769075.jpeg?auto=compress&cs=tinysrgb&w=1600",
  ];

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState(localStorage.getItem("rememberEmail") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(!!localStorage.getItem("rememberEmail"));
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);

  // Initialize Carousel
  useEffect(() => {
    const el = document.getElementById("bgCarousel");
    if (!el) return;
    const existing = Carousel.getInstance(el);
    const instance = existing || new Carousel(el, {
        interval: 5000, // Slower for elegance
        ride: "carousel",
        pause: false,
        wrap: true,
        touch: true,
        keyboard: false,
      });
    instance.cycle();
  }, []);

  function logout() {
    ["token", "email", "name", "organization", "userId", "role", "expiresAt"]
      .forEach(k => localStorage.removeItem(k));

    window.location.href = "/";
  }


  const emailValid = /\S+@\S+\.\S+/.test(email);
  const passwordValid = password.length > 0;

  const loginSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");

    if (!emailValid || !passwordValid) {
      setLoginError("Please enter a valid email and password.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Invalid credentials");

      if (data?.token) localStorage.setItem("token", data.token);
      localStorage.setItem("email", email);

      if (remember) localStorage.setItem("rememberEmail", email);
      else localStorage.removeItem("rememberEmail");

      window.location.href = data?.redirect || "/Homepage";
    } catch (err) {
      setLoginError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>

      {/* Background Carousel */}
      <div className="bg-carousel">
        <div id="bgCarousel" className="carousel slide carousel-fade" data-bs-ride="carousel">
          <div className="carousel-inner">
            {IMAGES.map((src, i) => (
              <div key={i} className={`carousel-item ${i === 0 ? "active" : ""}`}>
                <img src={src} alt="" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cinematic Overlay */}
      <div className="bg-scrim" />

      {/* Main Layout */}
      <main className="auth-center container-xxl">
        <div className="wrapper">
          
          {/* Header */}
          <div className="brand-row">
            <div className="brand-logo">
              <i className="bi bi-globe-americas"></i>
            </div>
            <h1 className="brand-title">Terra LandX</h1>
            <p className="brand-subtitle">Welcome back, please login</p>
          </div>

          {/* Error Message */}
          {loginError && (
            <div className="error-box">
              <i className="bi bi-exclamation-circle me-2"></i>
              {loginError}
            </div>
          )}

          {/* Form */}
          <form onSubmit={loginSubmit} noValidate>
            
            {/* Email Field */}
            <div className="input-group-modern">
              <input
                type="email"
                placeholder="Email address"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <i className="bi bi-envelope input-icon"></i>
            </div>

            {/* Password Field */}
            <div className="input-group-modern">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <i className="bi bi-lock input-icon"></i>
              <button
                type="button"
                className="toggle-visibility"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
              </button>
            </div>

            {/* Remember & Forgot */}
            <div className="action-row">
              <label className="custom-check">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Remember me
              </label>
              <a href="/ForgotPassword">Forgot password?</a>
            </div>

            {/* Submit Button */}
            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Authenticating...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <div className="footer-note">
            &copy; {new Date().getFullYear()} Terra Drone Systems
          </div>
        </div>
      </main>
    </>
  );
}

export default App;